import * as THREE from 'three';
import Grid from 'Geodata/grid';
import { getViewer } from 'store/viewerStore';
import usePersistentStore, { getElement, getGlobalState } from '@gui/store/persistent/persistentStore';
import { getDensityRenderScale } from '@data/Simulations/pipeline/blocks/DensityBlock';

export class SensorPointPicker {
    public pickAndSelect(event: MouseEvent, canvas: HTMLCanvasElement): boolean {
        const viewer = getViewer();
        const instanceRenderer = viewer.getInstanceRenderer?.();
        if (!instanceRenderer) return false;

        // Get active sensor meshes and indices:
        const pickables = instanceRenderer.getPickableSensorMeshes().filter((mesh) => mesh.visible);
        const pickableIndices = instanceRenderer.getPickableSensorIndices();
        if (pickables.length === 0 || pickableIndices.size === 0) return false;

        // Raycast from mouse position into the scene and find the closest intersected sensor point:
        const rect = canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;

        const pointer = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        const camera = viewer.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type);
        raycaster.setFromCamera(pointer, camera);
        const sensorIndex = this.pickByRotatedGridIntersection(raycaster, viewer.scene.matrixWorld, pickableIndices);

        if (typeof sensorIndex !== 'number') return false;
        const sensorPoint = Grid.sensorPoints?.[sensorIndex];
        if (!sensorPoint || sensorPoint.getID() === -1) return false;

        // Update Selected Sensor Point in the store:
        const [x, y, z] = Grid.indexToXYZ(sensorIndex);
        usePersistentStore.getState().setSimulationItems({ x, y, z });
        return true;
    }

    private pickByRotatedGridIntersection(
        raycaster: THREE.Raycaster,
        sceneMatrixWorld: THREE.Matrix4,
        pickableIndices: ReadonlySet<number>
    ): number | null {
        // Setup for raycasting:
        const element = getElement();
        const densityScale = getDensityRenderScale(element?.ELEMENT_CURRENT_DENSITY);
        const voxelHalfSize = (Grid.gridRes * densityScale) / 2;
        const half = voxelHalfSize;
        const rotationAngle = THREE.MathUtils.degToRad(-Grid.gridAngle);
        const hasRotation = Math.abs(rotationAngle) > 1e-9;
        const rotationOrigin = new THREE.Vector3(Grid.gridOrigin.x, Grid.gridOrigin.y, Grid.gridOrigin.z);
        const rotationAxis = new THREE.Vector3(0, 0, 1);
        const inverseSceneMatrix = new THREE.Matrix4().copy(sceneMatrixWorld).invert();
        const localRayOrigin = raycaster.ray.origin.clone().applyMatrix4(inverseSceneMatrix);
        const localRayDirection = raycaster.ray.direction.clone().transformDirection(inverseSceneMatrix);

        // Apply gridAngle:
        if (hasRotation) {
            localRayOrigin.sub(rotationOrigin).applyAxisAngle(rotationAxis, rotationAngle).add(rotationOrigin);
            localRayDirection.applyAxisAngle(rotationAxis, rotationAngle);
        }

        // Start raycasting against the sensor points (voxels), and find the closest hit:
        const localRay = new THREE.Ray(localRayOrigin, localRayDirection);
        const localCenter = new THREE.Vector3();
        const rotatedCenter = new THREE.Vector3();
        const boxMin = new THREE.Vector3();
        const boxMax = new THREE.Vector3();
        const box3 = new THREE.Box3();
        const hitPoint = new THREE.Vector3();
        const originToHit = new THREE.Vector3();

        let bestSensorIndex: number | null = null;
        let bestDistance2 = Number.POSITIVE_INFINITY;

        for (const sensorIndex of pickableIndices) {
            const sensor = Grid.sensorPoints?.[sensorIndex];
            if (!sensor || sensor.getID() === -1) continue;
            const center = sensor.getCenter();
            localCenter.set(center.x, center.y, center.z);
            rotatedCenter.copy(localCenter);
            if (hasRotation) {
                rotatedCenter.sub(rotationOrigin).applyAxisAngle(rotationAxis, rotationAngle).add(rotationOrigin);
            }

            boxMin.copy(rotatedCenter).addScalar(-half);
            boxMax.copy(rotatedCenter).addScalar(half);
            box3.set(boxMin, boxMax);

            const hit = localRay.intersectBox(box3, hitPoint);
            if (!hit) continue;

            originToHit.copy(hitPoint).sub(localRay.origin);
            const distance2 = originToHit.lengthSq();
            if (distance2 < bestDistance2) {
                bestDistance2 = distance2;
                bestSensorIndex = sensorIndex;
            }
        }

        return bestSensorIndex;
    }
}

export default SensorPointPicker;
